import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import {
  Button,
  Collapse,
  Divider,
  Drawer,
  Slider,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Radio,
  Grid,
  IconButton,
  InputAdornment
} from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import {
  applyFilter,
  setFilterValue,
  getFilterValues,
  clearFilterValues
} from 'actions';
import {
  CLOTHING_TYPES,
  GENDERS,
  EVENT_VALIDATION_STATUSES
} from 'common/constants';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  drawer: {
    width: 420,
    maxWidth: '100%'
  },
  header: {
    padding: theme.spacing(2, 1),
    display: 'flex',
    justifyContent: 'space-between'
  },
  buttonIcon: {
    marginRight: theme.spacing(1)
  },
  content: {
    padding: theme.spacing(0, 3),
    flexGrow: 1
  },
  contentSection: {
    padding: theme.spacing(2, 0)
  },
  contentSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    cursor: 'pointer'
  },
  contentSectionContent: {
    paddingTop: theme.spacing(2)
  },
  formGroup: {
    padding: theme.spacing(1, 0)
  },
  fieldGroup: {
    display: 'flex',
    alignItems: 'center'
  },
  field: {
    marginTop: 0,
    marginBottom: 0
  },
  flexGrow: {
    flexGrow: 1
  },
  addButton: {
    marginLeft: theme.spacing(1)
  },
  tags: {
    marginTop: theme.spacing(1)
  },
  minAmount: {
    marginRight: theme.spacing(3)
  },
  maxAmount: {
    marginLeft: theme.spacing(3)
  },
  radioGroup: {},
  actions: {
    padding: theme.spacing(3),
    '& > * + *': {
      marginTop: theme.spacing(2)
    }
  }
}));

const WalkListFilter = props => {
  const {
    open,
    onClose,
    onApply,
    page,
    filter,
    scoreFilterHidden,
    className,
    ...rest
  } = props;

  const classes = useStyles();
  const dispatch = useDispatch();

  const [expandGeneral, setExpandGeneral] = useState(true);
  const [expandDataValidation, setExpandDataValidation] = useState(false);

  useEffect(() => {
    let mounted = true;

    if (mounted && open) {
      dispatch(getFilterValues(page));
    }

    return () => {
      mounted = false;
    };
  }, [open]);

  const handleClear = () => {
    dispatch(clearFilterValues(page));
  };

  const walkIdInput = useRef(null);
  const walkIdClickFocusing = useRef(false);

  const handleFieldChange = (field, value) => {
    dispatch(
      setFilterValue(page, {
        [field]: value
      })
    );
  };

  const handleObjectPresentFieldChange = (field, value) => {
    /* If turning off active radio button */
    if (!value && filter[field]) {
      dispatch(
        setFilterValue(page, {
          [field]: value
        })
      );
    } else {
      if (field === 'objectPresent') {
        dispatch(
          setFilterValue(page, {
            ['objectPresent']: value
          })
        );
        dispatch(
          setFilterValue(page, {
            ['objectNotPresent']: !value
          })
        );
      } else if (field === 'objectNotPresent') {
        dispatch(
          setFilterValue(page, {
            ['objectNotPresent']: value
          })
        );
        dispatch(
          setFilterValue(page, {
            ['objectPresent']: !value
          })
        );
      }
    }
  };

  const handleAlertDetectedFieldChange = value => {
    /* Clicking the active radio clears the filter */
    dispatch(
      setFilterValue(page, {
        alertDetected: filter.alertDetected === value ? null : value
      })
    );
  };

  const handleToggleGeneral = () => {
    setExpandGeneral(expandGeneral => !expandGeneral);
  };

  const handleToggleDataValidation = () => {
    setExpandDataValidation(expandDataValidation => !expandDataValidation);
  };

  const handleSubmit = event => {
    event.preventDefault();
    onFilter && onFilter();
  };

  const onFilter = () => {
    Promise.resolve(dispatch(applyFilter(page, filter))).then(() => {
      onClose();
      onApply();
      dispatch(getFilterValues(page));
    });
  };

  return (
    <Drawer
      anchor="right"
      classes={{ paper: classes.drawer }}
      onClose={onClose}
      open={open}
      variant="temporary">
      <form
        {...rest}
        className={clsx(classes.root, className)}
        onSubmit={handleSubmit}>
        <div className={classes.header}>
          <Button onClick={onClose} size="small">
            <CloseIcon className={classes.buttonIcon} />
            Close
          </Button>
        </div>
        <div className={classes.content}>
          <div className={classes.contentSection}>
            <div
              className={classes.contentSectionHeader}
              onClick={handleToggleGeneral}>
              <Typography variant="h5">General</Typography>
              {expandGeneral ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </div>
            <Divider />
            <Collapse in={expandGeneral}>
              <div className={classes.contentSectionContent}>
                <div className={classes.formGroup}>
                  <TextField
                    className={classes.select}
                    margin="dense"
                    fullWidth
                    label="Validation Status"
                    name="validationStatus"
                    onChange={event =>
                      handleFieldChange('validationStatus', event.target.value)
                    }
                    select
                    // eslint-disable-next-line react/jsx-sort-props
                    SelectProps={{ native: true }}
                    value={
                      filter.validationStatus ? filter.validationStatus : ''
                    }
                    variant="outlined">
                    <option></option>
                    {Object.keys(EVENT_VALIDATION_STATUSES).map(
                      validationStatus => (
                        <option key={validationStatus} value={validationStatus}>
                          {EVENT_VALIDATION_STATUSES[validationStatus]}
                        </option>
                      )
                    )}
                  </TextField>
                </div>
                <div className={classes.formGroup}>
                  <TextField
                    className={classes.field}
                    fullWidth
                    label="Face Recognition Name"
                    margin="dense"
                    name="facialRecognitionPersonName"
                    onChange={event =>
                      handleFieldChange(
                        'facialRecognitionPersonName',
                        event.target.value
                      )
                    }
                    value={
                      filter.facialRecognitionPersonName !== null
                        ? filter.facialRecognitionPersonName
                        : ''
                    }
                    variant="outlined"
                  />
                </div>
                <div className={classes.formGroup}>
                  <TextField
                    className={classes.select}
                    fullWidth
                    margin="dense"
                    label="Gender"
                    name="participantGender"
                    onChange={event =>
                      handleFieldChange('participantGender', event.target.value)
                    }
                    select
                    // eslint-disable-next-line react/jsx-sort-props
                    SelectProps={{ native: true }}
                    value={
                      filter.participantGender ? filter.participantGender : ''
                    }
                    variant="outlined">
                    <option value="" />

                    {Object.keys(GENDERS)
                      .filter(g => g !== 'NON_BINARY')
                      .map(gender => (
                        <option key={gender} value={gender}>
                          {GENDERS[gender]}
                        </option>
                      ))}
                  </TextField>
                </div>
                <div className={classes.formGroup}>
                  <TextField
                    className={classes.select}
                    fullWidth
                    margin="dense"
                    label="Clothing Type"
                    name="annotatedParticipantClothingType"
                    onChange={event =>
                      handleFieldChange(
                        'annotatedParticipantClothingType',
                        event.target.value
                      )
                    }
                    select
                    // eslint-disable-next-line react/jsx-sort-props
                    SelectProps={{ native: true }}
                    value={
                      filter.annotatedParticipantClothingType
                        ? filter.annotatedParticipantClothingType
                        : ''
                    }
                    variant="outlined">
                    <option value="" />

                    {Object.keys(CLOTHING_TYPES).map(clothingType => (
                      <option key={clothingType} value={clothingType}>
                        {CLOTHING_TYPES[clothingType]}
                      </option>
                    ))}
                  </TextField>
                </div>
                <div>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filter.specialInterest ? true : false}
                        color="primary"
                        onChange={(event, value) =>
                          handleFieldChange('specialInterest', value)
                        }
                      />
                    }
                    label="Special Interest"
                  />
                </div>
                <div className={classes.formGroup}>
                  <Typography component="p" gutterBottom variant="overline">
                    Object
                  </Typography>
                  <div className={classes.fieldGroup}>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={filter.objectPresent}
                          color="primary"
                          onClick={(event, value) =>
                            handleObjectPresentFieldChange(
                              'objectPresent',
                              !filter.objectPresent
                            )
                          }
                        />
                      }
                      label="Object Present"
                    />
                  </div>
                  <div className={classes.fieldGroup}>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={filter.objectNotPresent}
                          color="primary"
                          onClick={(event, value) =>
                            handleObjectPresentFieldChange(
                              'objectNotPresent',
                              !filter.objectNotPresent
                            )
                          }
                        />
                      }
                      label="Object Not Present"
                    />
                  </div>
                </div>
                <div className={classes.formGroup}>
                  <Typography component="p" gutterBottom variant="overline">
                    Alerts
                  </Typography>
                  <div className={classes.fieldGroup}>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={filter.alertDetected === true}
                          color="primary"
                          onClick={() => handleAlertDetectedFieldChange(true)}
                        />
                      }
                      label="Alert"
                    />
                  </div>
                  <div className={classes.fieldGroup}>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={filter.alertDetected === false}
                          color="primary"
                          onClick={() => handleAlertDetectedFieldChange(false)}
                        />
                      }
                      label="Non-Alert"
                    />
                  </div>
                </div>
                <div className={classes.formGroup}>
                  <TextField
                    className={classes.field}
                    fullWidth
                    InputProps={{
                      endAdornment: filter.dataPointId ? (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="Clear walk id"
                            edge="end"
                            onClick={() => {
                              handleFieldChange('dataPointId', '');
                              if (walkIdInput.current)
                                walkIdInput.current.focus();
                            }}
                            size="small">
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }}
                    inputProps={{
                      onMouseDown: event => {
                        walkIdClickFocusing.current =
                          document.activeElement !== event.target;
                      },
                      onMouseUp: event => {
                        if (walkIdClickFocusing.current) event.preventDefault();
                        walkIdClickFocusing.current = false;
                      }
                    }}
                    inputRef={walkIdInput}
                    label="Walk Id"
                    margin="dense"
                    name="dataPointId"
                    onChange={event =>
                      handleFieldChange('dataPointId', event.target.value)
                    }
                    onFocus={event => event.target.select()}
                    value={filter.dataPointId}
                    variant="outlined"
                  />
                </div>
                {!scoreFilterHidden && (
                  <div className={classes.formGroup}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          className={classes.field}
                          fullWidth
                          label="Score Min"
                          margin="dense"
                          name="asWalkedMaxTorsoScoreMin"
                          onChange={event =>
                            handleFieldChange(
                              'asWalkedMaxTorsoScoreMin',
                              event.target.value
                            )
                          }
                          value={filter.asWalkedMaxTorsoScoreMin}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          className={classes.field}
                          fullWidth
                          label="Score Max"
                          margin="dense"
                          name="asWalkedMaxTorsoScoreMax"
                          onChange={event =>
                            handleFieldChange(
                              'asWalkedMaxTorsoScoreMax',
                              event.target.value
                            )
                          }
                          value={filter.asWalkedMaxTorsoScoreMax}
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                  </div>
                )}
              </div>
            </Collapse>
          </div>

          <div className={classes.contentSection}>
            <div
              className={classes.contentSectionHeader}
              onClick={handleToggleDataValidation}>
              <Typography variant="h5">Data Validation</Typography>
              {expandDataValidation ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </div>
            <Divider />
            <Collapse in={expandDataValidation}>
              <div className={classes.contentSectionContent}>
                <div>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          filter.missingAnnotatedParticipantGender
                            ? true
                            : false
                        }
                        color="primary"
                        onChange={(event, value) =>
                          handleFieldChange(
                            'missingAnnotatedParticipantGender',
                            value
                          )
                        }
                      />
                    }
                    label="Missing Gender"
                  />
                </div>
                <div>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          filter.missingAnnotatedParticipantClothingType
                            ? true
                            : false
                        }
                        color="primary"
                        onChange={(event, value) =>
                          handleFieldChange(
                            'missingAnnotatedParticipantClothingType',
                            value
                          )
                        }
                      />
                    }
                    label="Missing Clothing Type"
                  />
                </div>
              </div>
            </Collapse>
          </div>

          {/* <div className={classes.contentSection}>
            <div
              className={classes.contentSectionHeader}
              onClick={handleToggleObjectPrediction}
            >
              <Typography variant="h5">Object Prediction</Typography>
              {expandObjectPrediction ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </div>
            <Divider />
            <Collapse in={expandObjectPrediction}>
              <div className={classes.contentSectionContent}>
                <div className={classes.formGroup}>
                  <Typography
                      component="p"
                      gutterBottom
                      variant="overline"
                  >
                      Object
                  </Typography>
                  <div className={classes.fieldGroup}>
                      <Typography
                      className={classes.minAmount}
                      variant="body1"
                      >
                      {filter.objectPercent[0]}%
                      </Typography>
                      <Slider
                      className={classes.flexGrow}
                      max={100}
                      min={0}
                      onChange={(event, value) =>
                          handleFieldChange('objectPercent', value)
                      }
                      value={filter.objectPercent}
                      valueLabelDisplay="auto"
                      />
                      <Typography
                      className={classes.maxAmount}
                      variant="body1"
                      >
                      {filter.objectPercent[1]}%
                      </Typography>
                  </div>
                </div>
                <div className={classes.formGroup}>
                    <Typography
                        component="p"
                        gutterBottom
                        variant="overline"
                    >
                        Object Left
                    </Typography>
                    <div className={classes.fieldGroup}>
                        <Typography
                        className={classes.minAmount}
                        variant="body1"
                        >
                        {filter.objectLeftPercent[0]}%
                        </Typography>
                        <Slider
                        className={classes.flexGrow}
                        max={100}
                        min={0}
                        onChange={(event, value) =>
                            handleFieldChange('objectLeftPercent', value)
                        }
                        value={filter.objectLeftPercent}
                        valueLabelDisplay="auto"
                        />
                        <Typography
                        className={classes.maxAmount}
                        variant="body1"
                        >
                        {filter.objectLeftPercent[1]}%
                        </Typography>
                    </div>
                </div>
                <div className={classes.formGroup}>
                    <Typography
                        component="p"
                        gutterBottom
                        variant="overline"
                    >
                        Object Right
                    </Typography>
                    <div className={classes.fieldGroup}>
                        <Typography
                        className={classes.minAmount}
                        variant="body1"
                        >
                        {filter.objectRightPercent[0]}%
                        </Typography>
                        <Slider
                        className={classes.flexGrow}
                        max={100}
                        min={0}
                        onChange={(event, value) =>
                            handleFieldChange('objectRightPercent', value)
                        }
                        value={filter.objectRightPercent}
                        valueLabelDisplay="auto"
                        />
                        <Typography
                          className={classes.maxAmount}
                          variant="body1"
                        >
                        {filter.objectRightPercent[1]}%
                        </Typography>
                    </div>
                </div>
              </div>
            </Collapse>
          </div> */}
        </div>
        <div className={classes.actions}>
          <Button fullWidth onClick={handleClear} variant="contained">
            <DeleteIcon className={classes.buttonIcon} />
            Clear
          </Button>
          <Button color="primary" fullWidth type="submit" variant="contained">
            Apply filters
          </Button>
        </div>
      </form>
    </Drawer>
  );
};

WalkListFilter.propTypes = {
  className: PropTypes.string,
  filter: PropTypes.object.isRequired,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  open: PropTypes.bool.isRequired,
  page: PropTypes.string.isRequired,
  scoreFilterHidden: PropTypes.bool
};

export default WalkListFilter;
